import { useTranslation } from 'react-i18next'
import { useHotkeys } from 'react-hotkeys-hook'
import { ChevronDown, ChevronLeft, ChevronUp, Copy, Link2, Maximize2, X } from 'lucide-react'
import { toast } from 'sonner'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

/**
 * What the container around a task (modal, side panel, full page) hands to
 * TaskDetails so it can draw the shared header (design 16b): breadcrumb + ID,
 * position in the list, ↑↓, then star · link · expand · ⋯ · close.
 */
export interface TaskDetailsChrome {
  /** 1-based position and list length, e.g. 9 / 29. */
  position?: { index: number; total: number }
  onNavigateUp?: () => void
  onNavigateDown?: () => void
  canNavigateUp?: boolean
  canNavigateDown?: boolean
  /** Open the full page; hidden when already there. */
  onExpand?: () => void
  onClose?: () => void
  /** Phones only (design 36b): ‹ at the start of the header on the full page. */
  onBack?: () => void
}

interface TaskDetailsHeaderProps {
  chrome: TaskDetailsChrome
  projectLabel: ReactNode
  identifier: string | null
  taskUrl: string | null
  /** Star toggle and ⋯ menu, owned by TaskDetails. */
  star: ReactNode
  menu: ReactNode
  /** The full page's header sits on the paper chrome; modal and panel are white (design 30). */
  surface?: 'paper' | 'card'
}

const iconButton =
  'inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground disabled:pointer-events-none disabled:opacity-40 phone:size-10'

export function TaskDetailsHeader({ chrome, projectLabel, identifier, taskUrl, star, menu, surface = 'card' }: TaskDetailsHeaderProps) {
  const { t } = useTranslation('tasks')

  // ⌘↵ opens the full page (design 24a). Skipped while a picker is open,
  // since the date picker uses the same chord to save.
  useHotkeys(
    'mod+enter',
    (event) => {
      if (!chrome.onExpand || document.querySelector('[data-radix-popper-content-wrapper]')) return
      event.preventDefault()
      chrome.onExpand()
    },
    { enabled: Boolean(chrome.onExpand) },
    [chrome.onExpand],
  )

  return (
    <div
      className={cn(
        'sticky top-0 z-20 flex h-11 shrink-0 items-center gap-3 px-4 phone:h-14 phone:gap-1 phone:px-2',
        surface === 'paper' ? 'bg-background' : 'border-b bg-card',
      )}
    >
      {chrome.onBack && (
        <button
          type="button"
          onClick={chrome.onBack}
          aria-label={t('detailHeader.back')}
          className={cn(iconButton, 'hidden phone:inline-flex')}
        >
          <ChevronLeft className="size-5" />
        </button>
      )}
      <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5 truncate">{projectLabel}</span>
        {identifier && (
          <>
            <span aria-hidden className="text-muted-foreground/50">
              /
            </span>
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard.writeText(identifier)
                toast.success(t('detailHeader.copiedId', { id: identifier }))
              }}
              title={t('detailHeader.copyId')}
              className="inline-flex h-6 items-center gap-1.5 rounded-md px-1 font-mono text-[12px] text-foreground/80 transition-colors hover:bg-secondary"
            >
              {identifier}
              <Copy aria-hidden className="size-3 text-muted-foreground" />
            </button>
          </>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {chrome.position && (
          <span className="mr-1 text-[12px] tabular-nums text-muted-foreground">
            {t('detailHeader.position', { index: chrome.position.index, total: chrome.position.total })}
          </span>
        )}
        {(chrome.onNavigateUp || chrome.onNavigateDown) && (
          <>
            <button
              type="button"
              onClick={chrome.onNavigateUp}
              disabled={!chrome.canNavigateUp}
              aria-label={t('detailHeader.previous')}
              title={t('detailHeader.previous')}
              className={iconButton}
            >
              <ChevronUp className="size-4" />
            </button>
            <button
              type="button"
              onClick={chrome.onNavigateDown}
              disabled={!chrome.canNavigateDown}
              aria-label={t('detailHeader.next')}
              title={t('detailHeader.next')}
              className={iconButton}
            >
              <ChevronDown className="size-4" />
            </button>
            <span aria-hidden className="mx-1.5 h-4 w-px bg-border" />
          </>
        )}
        {star}
        {taskUrl && (
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard.writeText(taskUrl)
              toast.success(t('detailHeader.copiedLink'))
            }}
            aria-label={t('detailHeader.copyLink')}
            title={t('detailHeader.copyLink')}
            className={iconButton}
          >
            <Link2 className="size-4" />
          </button>
        )}
        {chrome.onExpand && (
          <button
            type="button"
            onClick={chrome.onExpand}
            aria-label={t('detailHeader.expand')}
            title={`${t('detailHeader.expand')} (⌘↵)`}
            className={iconButton}
          >
            <Maximize2 className="size-3.5" />
          </button>
        )}
        {menu}
        {chrome.onClose && (
          <button
            type="button"
            onClick={chrome.onClose}
            aria-label={t('detailHeader.close')}
            title={t('detailHeader.close')}
            className={iconButton}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    </div>
  )
}
